import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "demo@test.com")
  .split(",")
  .map((e) => e.trim().toLowerCase());

export default function AdminSeo() {
  const { user } = useAuth();
  const [tab, setTab] = useState("global");
  const [global, setGlobal] = useState({
    ga4_id: "",
    gsc_verification: "",
    bing_verification: "",
    default_og_image: "",
    default_keywords: "",
    organization_name: "WriterSpark",
    organization_email: "",
    twitter_handle: "",
  });
  const [pages, setPages] = useState({});
  const [pageForm, setPageForm] = useState({
    path: "/",
    title: "",
    description: "",
    keywords: "",
    og_image: "",
    canonical: "",
    extra_jsonld: "",
  });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/seo/settings");
      setGlobal({ ...global, ...(data.global || {}) });
      setPages(data.pages || {});
    } catch { /* noop */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_EMAILS.includes((user.email || "").toLowerCase())) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>Admin access required</h1>
        <p className="text-slate-600 text-sm mb-6">Only allowlisted admin accounts can edit SEO settings.</p>
        <Link to="/app" className="text-indigo-600 font-bold">Back to dashboard</Link>
      </div>
    );
  }

  const saveGlobal = async () => {
    setBusy(true);
    try {
      await api.put("/seo/settings/global", global);
      toast.success("Global SEO saved");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const savePage = async () => {
    if (!pageForm.path.trim()) { toast.error("Path is required"); return; }
    setBusy(true);
    try {
      await api.put("/seo/settings/page", pageForm);
      toast.success(`Saved ${pageForm.path}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const deletePage = async (path) => {
    if (!window.confirm(`Delete override for ${path}?`)) return;
    try {
      await api.delete(`/seo/settings/page?path=${encodeURIComponent(path)}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const loadPage = (p) => {
    setPageForm({
      path: p.path || "",
      title: p.title || "",
      description: p.description || "",
      keywords: p.keywords || "",
      og_image: p.og_image || "",
      canonical: p.canonical || "",
      extra_jsonld: p.extra_jsonld || "",
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-seo-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>SEO Settings</h1>
        <p className="text-slate-600 text-sm mt-1">Override per-page meta. Globals apply site-wide. Leave blank to fall back to <code>seoConfig.js</code>.</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("global")} data-testid="seo-tab-global"
          className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "global" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"}`}>Global</button>
        <button onClick={() => setTab("pages")} data-testid="seo-tab-pages"
          className={`rounded-full px-4 py-2 text-sm font-bold ${tab === "pages" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"}`}>Per-page overrides</button>
      </div>

      {tab === "global" ? (
        <div className="tactile-card p-6 space-y-4 max-w-3xl">
          <Field label="GA4 Tracking ID" placeholder="G-XXXXXXX" value={global.ga4_id} onChange={(v) => setGlobal({ ...global, ga4_id: v })} testId="ga4-id"/>
          <Field label="Google Search Console verification (content)" placeholder="abc123..." value={global.gsc_verification} onChange={(v) => setGlobal({ ...global, gsc_verification: v })} testId="gsc-verify"/>
          <Field label="Bing Webmaster verification (content)" placeholder="abc123..." value={global.bing_verification} onChange={(v) => setGlobal({ ...global, bing_verification: v })} testId="bing-verify"/>
          <Field label="Default OG image URL" placeholder="https://..." value={global.default_og_image} onChange={(v) => setGlobal({ ...global, default_og_image: v })} testId="default-og-image"/>
          <Field label="Default keywords" value={global.default_keywords} onChange={(v) => setGlobal({ ...global, default_keywords: v })} testId="default-keywords"/>
          <Field label="Organization name" value={global.organization_name} onChange={(v) => setGlobal({ ...global, organization_name: v })} testId="org-name"/>
          <Field label="Organization email" value={global.organization_email} onChange={(v) => setGlobal({ ...global, organization_email: v })} testId="org-email"/>
          <Field label="Twitter handle (@yourbrand)" value={global.twitter_handle} onChange={(v) => setGlobal({ ...global, twitter_handle: v })} testId="twitter-handle"/>

          <button data-testid="save-global"
            disabled={busy} onClick={saveGlobal}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-5 py-2.5 disabled:opacity-60">
            <Save size={14}/> Save global SEO
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          <div className="space-y-2">
            <button data-testid="seo-page-new" onClick={() => loadPage({ path: "/" })}
              className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-2xl px-4 py-2.5 text-sm">
              <Plus size={14}/> New override
            </button>
            {Object.entries(pages).length === 0 ? (
              <p className="text-xs text-slate-500 mt-3">No page overrides yet.</p>
            ) : Object.entries(pages).map(([path, p]) => (
              <div key={path} className="tactile-card p-3 flex items-center justify-between">
                <button onClick={() => loadPage(p)}
                  className="text-sm font-mono font-bold text-left flex-1 truncate"
                  data-testid={`seo-page-${path.replace(/\//g, "_")}`}>
                  {path}
                </button>
                <button onClick={() => deletePage(path)} className="text-slate-400 hover:text-rose-600 p-1"
                  data-testid={`seo-page-del-${path.replace(/\//g, "_")}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            ))}
          </div>

          <div className="tactile-card p-6 space-y-4">
            <Field label="Path" placeholder="/some-page" value={pageForm.path} onChange={(v) => setPageForm({ ...pageForm, path: v })} testId="seo-form-path"/>
            <Field label="Title" value={pageForm.title} onChange={(v) => setPageForm({ ...pageForm, title: v })} testId="seo-form-title"/>
            <Field label="Description" textarea rows={3} value={pageForm.description} onChange={(v) => setPageForm({ ...pageForm, description: v })} testId="seo-form-desc"/>
            <Field label="Keywords" value={pageForm.keywords} onChange={(v) => setPageForm({ ...pageForm, keywords: v })} testId="seo-form-kw"/>
            <Field label="OG image URL" value={pageForm.og_image} onChange={(v) => setPageForm({ ...pageForm, og_image: v })} testId="seo-form-og"/>
            <Field label="Canonical URL (optional)" value={pageForm.canonical} onChange={(v) => setPageForm({ ...pageForm, canonical: v })} testId="seo-form-canonical"/>
            <Field label="Extra JSON-LD schema (raw JSON string)" textarea rows={5} value={pageForm.extra_jsonld} onChange={(v) => setPageForm({ ...pageForm, extra_jsonld: v })} testId="seo-form-schema"/>
            <button data-testid="seo-form-save" disabled={busy} onClick={savePage}
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-5 py-2.5 disabled:opacity-60">
              <Save size={14}/> Save override
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder, textarea, rows = 1, testId }) => (
  <label className="block">
    <span className="text-xs uppercase font-bold tracking-widest text-slate-500">{label}</span>
    {textarea ? (
      <textarea
        data-testid={testId}
        rows={rows}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-3 py-2 outline-none text-sm font-mono"
      />
    ) : (
      <input
        data-testid={testId}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-3 py-2 outline-none text-sm"
      />
    )}
  </label>
);
